import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Info, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { ThreadList } from '../../components/conversations/ThreadList';
import { ThreadFilters } from '../../components/conversations/ThreadFilters';
import { MessageList } from '../../components/conversations/MessageList';
import { ContactDetails } from '../../components/conversations/ContactDetails';
import { ThreadListQuery } from '../../types';

export function ConversationsPage() {
  const [selectedThreadId, setSelectedThreadId] = useState<string | undefined>();
  const [selectedSenderId, setSelectedSenderId] = useState<string | undefined>();
  const [showContactDetails, setShowContactDetails] = useState(false);
  const [filters, setFilters] = useState<ThreadListQuery>({});
  const [showMessages, setShowMessages] = useState(false); // For mobile view
  const [isRefreshing, setIsRefreshing] = useState(false);
  const queryClient = useQueryClient();

  const handleThreadSelect = (threadId: string, senderId: string) => {
    setSelectedThreadId(threadId);
    setSelectedSenderId(senderId);
    setShowMessages(true); // Show messages on mobile
  };

  const handleBackToList = () => {
    setShowMessages(false);
    setSelectedThreadId(undefined);
  };

  const handleShowContactDetails = (senderId: string) => {
    setSelectedSenderId(senderId);
    setShowContactDetails(true);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await queryClient.invalidateQueries({ queryKey: ['conversations-threads'] });
    if (selectedThreadId) {
      await queryClient.invalidateQueries({ queryKey: ['conversations-messages', selectedThreadId] });
    }
    setIsRefreshing(false);
  };

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]">
      {/* Header */}
      <div className="border-b px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Conversas</h1>
          <p className="text-sm text-muted-foreground">
            Visualize todas as conversas entre usuários e assistentes
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={isRefreshing}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
          Atualizar
        </Button>
      </div>

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left panel - Thread list (hidden on mobile when viewing messages) */}
        <div
          className={`w-full md:w-96 border-r flex flex-col ${
            showMessages ? 'hidden md:flex' : 'flex'
          }`}
        >
          {/* Filters */}
          <div className="p-4 border-b">
            <ThreadFilters onFiltersChange={setFilters} />
          </div>

          {/* Thread list with scroll */}
          <div className="flex-1 overflow-y-auto p-4">
            <ThreadList
              filters={filters}
              selectedThreadId={selectedThreadId}
              onThreadSelect={handleThreadSelect}
            />
          </div>
        </div>

        {/* Right panel - Messages (full screen on mobile) */}
        <div
          className={`flex-1 flex flex-col ${
            showMessages ? 'flex' : 'hidden md:flex'
          }`}
        >
          {/* Messages header */}
          {selectedThreadId && (
            <div className="border-b px-4 py-3 flex items-center gap-3">
              {/* Back button (mobile only) */}
              <Button
                variant="ghost"
                size="sm"
                className="md:hidden"
                onClick={handleBackToList}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>

              <div className="flex-1">
                <h2 className="font-semibold">Conversa selecionada</h2>
                <p className="text-xs text-muted-foreground">
                  {selectedThreadId}
                </p>
              </div>

              {/* Contact details button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  if (selectedSenderId) {
                    handleShowContactDetails(selectedSenderId);
                  }
                }}
                disabled={!selectedSenderId}
              >
                <Info className="h-4 w-4" />
              </Button>
            </div>
          )}

          {/* Messages list */}
          <MessageList threadId={selectedThreadId} />
        </div>
      </div>

      {/* Contact details drawer */}
      <ContactDetails
        senderId={selectedSenderId}
        isOpen={showContactDetails}
        onClose={() => setShowContactDetails(false)}
      />
    </div>
  );
}
